import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaBell, FaMoneyBillWave } from "react-icons/fa";
import moment from "moment";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetCheckUpComponent({ appointmentId, doctorId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petCheckUps, setPetCheckUps] = useState([]);
    const [checkUps, setCheckUps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCheckUp, setEditingCheckUp] = useState(null);
    const [selectedCheckUp, setSelectedCheckUp] = useState("");
    const [checkUpPrice, setCheckUpPrice] = useState(0);
    const [recommendation, setRecommendation] = useState("");
    const getTodayDateTimeLocal = () => {
        const now = new Date();
        const offset = now.getTimezoneOffset();
        const local = new Date(now.getTime() - offset * 60000);
        return local.toISOString().slice(0, 16);
    };

    const [checkUpDate, setCheckUpDate] = useState(getTodayDateTimeLocal());
    const [reminderDate, setReminderDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [hasCheckUpForAppointment, setHasCheckUpForAppointment] = useState(false);
    const [isRepeat, setIsRepeat] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '0';
        const num = parseFloat(price);
        if (isNaN(num)) return '0';
        return num % 1 === 0 ? num.toString() : num.toFixed(2);
    };

    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    useEffect(() => {
        fetchPetCheckUps();
    }, [appointmentId, petId]);

    const fetchPetCheckUps = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-check-ups/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const checkUpsForPet = response.data.pet_check_ups || [];
                setPetCheckUps(checkUpsForPet);
                setCheckUps(response.data.check_ups || []);
                setHasCheckUpForAppointment(
                    checkUpsForPet.some(item =>
                        String(item.appointment_id) === String(appointmentId)
                        || String(item.appointment?.id) === String(appointmentId)
                        || String(item.appointment_id || item.appointment?.id || '') === String(appointmentId)
                    )
                );
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet check-ups:", error);
            showErrorToast(t('failed to load check ups'));
            setPetCheckUps([]);
            setCheckUps([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckUpSelect = (selected) => {
        const checkUpId = selected?.value;
        setSelectedCheckUp(checkUpId || "");

        const checkUpItem = checkUps.find(item => item.id === checkUpId);
        if (checkUpItem) {
            setCheckUpPrice(checkUpItem.price || 0);
        } else {
            setCheckUpPrice(0);
        }
    };

    const handleAddCheckUp = () => {
        setEditingCheckUp(null);
        setSelectedCheckUp("");
        setCheckUpPrice(0);
        setRecommendation("");
        setCheckUpDate(getTodayDateTimeLocal());
        setReminderDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setIsRepeat(false);
        setShowModal(true);
    };

    const handleEditCheckUp = (checkUp) => {
        setEditingCheckUp(checkUp);
        setSelectedCheckUp(checkUp.check_up_id || checkUp.checkUp_id || (checkUp.checkUp?.id ?? ""));
        setCheckUpPrice(checkUp.price || 0);
        setRecommendation(checkUp.recomendation || "");
        setCheckUpDate(checkUp.date || "");
        setReminderDate(checkUp.reminder_date || "");
        setIsRepeat(false);

        if (checkUp.note && Array.isArray(checkUp.note) && checkUp.note.length > 0) {
            setGeneralNote(checkUp.note[0].note || "");
            setExistingNoteId(checkUp.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setShowModal(true);
    };

    const handleDeleteCheckUp = async (checkUpId) => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-check-ups-delete/${checkUpId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetCheckUps();
                showSuccessToast(t('Check up deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting check up:", error);
            showErrorToast(t('failed to delete check up'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (checkUpId, checkUpName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteCheckUp(checkUpId);
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCheckUp) {
            showErrorToast(t('please select check up'));
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingCheckUp
                ? `${process.env.REACT_APP_API_URL}/pet-check-ups-update/${editingCheckUp.id}`
                : `${process.env.REACT_APP_API_URL}/pet-check-ups`;

            const payload = {
                appointment_id: appointmentId,
                check_up_id: selectedCheckUp ? parseInt(selectedCheckUp) : null,
                price: checkUpPrice || null,
                recomendation: recommendation || null,
                date: checkUpDate || null,
                reminder_date: reminderDate || null,
            };

            if (isRepeat && editingCheckUp) {
                payload.is_repeat = true;
            }

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else if (existingNoteId) {
                payload.note = "";
                payload.note_id = existingNoteId;
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                showSuccessToast(editingCheckUp ? t('Check up updated successfully!') : t('Check up added successfully!'));
                fetchPetCheckUps();
            }
        } catch (error) {
            console.error("Error saving check up:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_check_up'));
        } finally {
            setSubmitting(false);
        }
    };

    const getCheckUpOptions = () => {
        return checkUps.map(item => ({
            value: item.id,
            label: `${item.name}`,
            price: item.price,
        }));
    };

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#e0e0e0' : 'white',
            color: '#333',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        })
    };

    const groupCheckUpsByAppointment = () => {
        const grouped = {};
        petCheckUps.forEach(checkUp => {
            const appointmentIdKey = checkUp.appointment_id;
            if (!grouped[appointmentIdKey]) {
                grouped[appointmentIdKey] = {
                    appointment: checkUp.appointment,
                    checkUps: []
                };
            }
            grouped[appointmentIdKey].checkUps.push(checkUp);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading check ups')}</p>
            </div>
        );
    }

    const checkUpOptions = getCheckUpOptions();
    const selectedOption = checkUpOptions.find(opt => opt.value === selectedCheckUp);
    const groupedCheckUps = groupCheckUpsByAppointment();

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('check ups list')}</h6>
                {isAppointmentOpen && !hasCheckUpForAppointment && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddCheckUp}
                        className="d-flex align-items-center gap-2"
                        style={{ backgroundColor: "#3182ce", borderColor: "#3182ce" }}
                    >
                        <FaPlus size={12} /> {t('add check up')}
                    </Button>
                )}
            </div>

            {!petCheckUps || petCheckUps.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaStethoscope size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no check ups found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupedCheckUps).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, checkUps: appointmentCheckUps } = appointmentData;

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentCheckUps.length} {t('check-up(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-3">
                                    {/* Directly map checkUps without nested accordion */}
                                    {appointmentCheckUps.map((checkUp) => {
                                        if (!checkUp) return null;
                                        const noteText = checkUp.note && Array.isArray(checkUp.note) && checkUp.note.length > 0
                                            ? checkUp.note[0].note
                                            : null;

                                        return (
                                            <div key={checkUp.id} className="card mb-3 border rounded">
                                                <div className="card-header bg-light py-3">
                                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                            <FaStethoscope className="text-primary" />

                                                            <Badge bg="info" className="px-3 py-2">
                                                                {checkUp.checkUp?.name || checkUp.check_up?.name || 'N/A'}
                                                            </Badge>
                                                            <div className="text-muted small">
                                                                👨‍⚕️ <strong>Doctor:</strong> Dr. {checkUp?.doctor?.name || 'Unknown Doctor'}
                                                            </div>
                                                            {checkUp.date && (
                                                                <div className="d-flex align-items-center gap-3 text-muted small">
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <FaCalendarAlt size={14} />
                                                                        {moment(checkUp.date).format("DD MMM YYYY")}
                                                                    </span>
                                                                    <span className="d-flex align-items-center gap-1">
                                                                        <FaBell size={14} />
                                                                        {moment(checkUp.date).format("hh:mm A")}
                                                                    </span>
                                                                </div>
                                                            )}

                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleEditCheckUp(checkUp)}
                                                                className="d-flex align-items-center gap-1"
                                                                style={{ background: '#3182ce', color: '#fff', border: 'none' }}
                                                            >
                                                                <FaEdit size={12} />
                                                                <span className="d-none d-sm-inline">{t('edit')}</span>
                                                            </Button>
                                                            {canDelete() && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteClick(checkUp.id, checkUp.checkUp?.name || checkUp.check_up?.name || 'Check Up')}
                                                                    className="d-flex align-items-center gap-1"
                                                                    style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none' }}
                                                                >
                                                                    <FaTrash size={12} />
                                                                    <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <Row className="g-4">
                                                        <Col md={12}>
                                                            <div className="p-3 bg-light rounded">
                                                                <div className="mb-2">
                                                                    <strong className="text-muted">{t('Check Up Name')}:</strong>{' '}
                                                                    {checkUp.checkUp?.name || checkUp.check_up?.name || 'N/A'}
                                                                </div>
                                                                {checkUp.checkUp?.description && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Description')}:</strong>{' '}
                                                                        {checkUp.checkUp.description}
                                                                    </div>
                                                                )}
                                                                {checkUp.date && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Check Up Date')}:</strong>{' '}
                                                                        {moment(checkUp.date).format("DD MMM YYYY hh:mm A")}
                                                                    </div>
                                                                )}
                                                                {checkUp.reminder_date && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                        {moment(checkUp.reminder_date).format("DD MMM YYYY hh:mm A")}
                                                                    </div>
                                                                )}
                                                                {noteText && (
                                                                    <div className="mt-3 pt-2 border-top">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <FaFileAlt className="text-success" />
                                                                            <strong className="text-muted">{t('note')}:</strong>
                                                                        </div>
                                                                        <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>
                        {editingCheckUp ? t('Edit Check Up') : t('Add Check Up')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Select Check Up')} <span className="text-danger">*</span></Form.Label>
                            <Select
                                options={checkUpOptions}
                                value={selectedOption}
                                onChange={handleCheckUpSelect}
                                placeholder={t('select check up')}
                                isSearchable
                                styles={customSelectStyles}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaMoneyBillWave className="text-success" /> {t('Price')}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={checkUpPrice === 0 ? '' : checkUpPrice}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setCheckUpPrice(val === '' ? '' : parseFloat(val));
                                }}
                                placeholder={t('Enter check up price')}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaCalendarAlt /> {t('Check Up Date')}
                            </Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={checkUpDate}
                                onChange={(e) => setCheckUpDate(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaBell /> {t('Reminder Date')}
                            </Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={reminderDate}
                                onChange={(e) => setReminderDate(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-3">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting} style={{ backgroundColor: "#3182ce", borderColor: "#3182ce" }}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingCheckUp ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}